/**
 * ErrorCountBadge.js - VERSÃO PREMIUM
 * 
 * Badge ultra-visual com efeitos de fogo, pulso e brilho
 * Design inspirado em games e notificações críticas
 */

import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Animated, Easing, Platform } from 'react-native';
import { Text } from '../ui/AppText';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Circle, Defs, RadialGradient, Stop } from 'react-native-svg';
import { SHORT_TERM_COLORS } from '../../constants/categories';

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

const ErrorCountBadge = ({
    count = 1,
    size = 'medium',
    showPulse = true,
    showGlow = true,
    showFlame = false,
    style
}) => {
    const pulseAnim = useRef(new Animated.Value(1)).current;
    const glowAnim = useRef(new Animated.Value(0.5)).current;
    const rotateAnim = useRef(new Animated.Value(0)).current;
    const shakeAnim = useRef(new Animated.Value(0)).current;

    // Determinar nível de urgência
    const getUrgencyLevel = () => {
        if (count >= 10) return 'critical';
        if (count >= 5) return 'high';
        if (count >= 3) return 'medium';
        return 'low';
    };

    const urgency = getUrgencyLevel();

    // Animação de pulse contínuo
    useEffect(() => {
        if (showPulse) {
            const pulseSpeed = urgency === 'critical' ? 600 : urgency === 'high' ? 800 : 1200;

            const pulse = Animated.loop(
                Animated.sequence([
                    Animated.timing(pulseAnim, {
                        toValue: urgency === 'critical' ? 1.25 : 1.15,
                        duration: pulseSpeed,
                        easing: Easing.out(Easing.ease),
                        useNativeDriver: Platform.OS !== 'web',
                    }),
                    Animated.timing(pulseAnim, {
                        toValue: 1,
                        duration: pulseSpeed,
                        easing: Easing.in(Easing.ease),
                        useNativeDriver: Platform.OS !== 'web',
                    }),
                ])
            );
            pulse.start();
            return () => pulse.stop();
        }
    }, [showPulse, urgency, pulseAnim]);

    // Animação de glow
    useEffect(() => {
        if (showGlow) {
            const glow = Animated.loop(
                Animated.sequence([
                    Animated.timing(glowAnim, {
                        toValue: 1,
                        duration: 1500,
                        useNativeDriver: Platform.OS !== 'web',
                    }),
                    Animated.timing(glowAnim, {
                        toValue: 0.3,
                        duration: 1500,
                        useNativeDriver: Platform.OS !== 'web',
                    }),
                ])
            );
            glow.start();
            return () => glow.stop();
        }
    }, [showGlow, glowAnim]);

    // Shake animation para crítico
    useEffect(() => {
        if (urgency === 'critical') {
            const shake = Animated.loop(
                Animated.sequence([
                    Animated.timing(shakeAnim, { toValue: 3, duration: 50, useNativeDriver: Platform.OS !== 'web' }),
                    Animated.timing(shakeAnim, { toValue: -3, duration: 50, useNativeDriver: Platform.OS !== 'web' }),
                    Animated.timing(shakeAnim, { toValue: 2, duration: 50, useNativeDriver: Platform.OS !== 'web' }),
                    Animated.timing(shakeAnim, { toValue: 0, duration: 50, useNativeDriver: Platform.OS !== 'web' }),
                    Animated.delay(2000),
                ])
            );
            shake.start();
            return () => shake.stop();
        }
    }, [urgency, shakeAnim]);

    // Cores por urgência
    const getColors = () => {
        switch (urgency) {
            case 'critical':
                return {
                    gradient: ['#FF0844', '#FFB199'],
                    bg: '#FF0844',
                    glow: '#FF0844',
                    text: '#FFFFFF',
                };
            case 'high':
                return {
                    gradient: ['#FF6B6B', '#FF8E8E'],
                    bg: '#FF6B6B',
                    glow: '#FF6B6B',
                    text: '#FFFFFF',
                };
            case 'medium':
                return {
                    gradient: ['#FF9F43', '#FFBE76'],
                    bg: '#FF9F43',
                    glow: '#FF9F43',
                    text: '#FFFFFF',
                };
            default:
                return {
                    gradient: ['#FFD93D', '#FFE66D'],
                    bg: '#FFD93D',
                    glow: '#FFD93D',
                    text: '#1A1A2E',
                };
        }
    };

    const colors = getColors();

    // Tamanhos
    const sizes = {
        small: { container: 28, font: 11, icon: 10, ring: 36 },
        medium: { container: 38, font: 14, icon: 12, ring: 48 },
        large: { container: 52, font: 18, icon: 16, ring: 64 },
        xlarge: { container: 68, font: 24, icon: 20, ring: 80 },
    };

    const currentSize = sizes[size] || sizes.medium;

    return (
        <Animated.View
            style={[
                styles.wrapper,
                {
                    transform: [
                        { scale: pulseAnim },
                        { translateX: shakeAnim },
                    ],
                },
                style,
            ]}
        >
            {/* Anel externo animado */}
            {showGlow && (
                <Animated.View
                    style={[
                        styles.glowRing,
                        {
                            width: currentSize.ring,
                            height: currentSize.ring,
                            borderRadius: currentSize.ring / 2,
                            borderColor: colors.glow,
                            opacity: glowAnim,
                        },
                    ]}
                />
            )}

            {/* Segundo anel (pulse) */}
            {urgency === 'critical' && (
                <Animated.View
                    style={[
                        styles.pulseRing,
                        {
                            width: currentSize.ring + 10,
                            height: currentSize.ring + 10,
                            borderRadius: (currentSize.ring + 10) / 2,
                            backgroundColor: colors.glow + '20',
                            transform: [{ scale: pulseAnim }],
                            opacity: glowAnim,
                        },
                    ]}
                />
            )}

            {/* Badge principal com gradiente */}
            <LinearGradient
                colors={colors.gradient}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={[
                    styles.container,
                    {
                        width: currentSize.container,
                        height: currentSize.container,
                        borderRadius: currentSize.container / 2,
                        shadowColor: colors.glow,
                    },
                ]}
            >
                {/* Ícone de X */}
                <Text style={[styles.icon, { fontSize: currentSize.icon }]}>✕</Text>

                {/* Número de erros */}
                <Text style={[styles.count, { fontSize: currentSize.font, color: colors.text }]}>
                    {count > 99 ? '99+' : count}
                </Text>
            </LinearGradient>

            {/* Indicador de chama para crítico */}
            {(urgency === 'critical' || showFlame) && (
                <View style={styles.flameContainer}>
                    <Text style={styles.flame}>🔥</Text>
                </View>
            )}

            {/* Labela de urgência */}
            {urgency === 'critical' && (
                <View style={styles.urgencyLabel}>
                    <Text style={styles.urgencyText}>CRÍTICO</Text>
                </View>
            )}
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    wrapper: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    glowRing: {
        position: 'absolute',
        borderWidth: 2,
    },
    pulseRing: {
        position: 'absolute',
    },
    container: {
        justifyContent: 'center',
        alignItems: 'center',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.5,
        shadowRadius: 12,
        elevation: 8,
    },
    icon: {
        position: 'absolute',
        top: 2,
        right: 2,
        color: 'rgba(255,255,255,0.8)',
        fontWeight: '700',
    },
    count: {
        fontWeight: '900',
        textAlign: 'center',
        textShadowColor: 'rgba(0,0,0,0.3)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 2,
    },
    flameContainer: {
        position: 'absolute',
        top: -8,
        right: -8,
    },
    flame: {
        fontSize: 16,
    },
    urgencyLabel: {
        position: 'absolute',
        bottom: -18,
        backgroundColor: '#FF0844',
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
    },
    urgencyText: {
        color: '#FFFFFF',
        fontSize: 8,
        fontWeight: '800',
        letterSpacing: 0.5,
    },
});

export default ErrorCountBadge;
